import { get } from 'lodash';
import { LANG_FONT_MAP } from '../constants/pdf';

/* There are nicer ways to get an arraybuffer but the others tried weren't IE 11 compatible */
export const base64ToArrayBuffer = base64 => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const ZERO_WIDTH_SPACE_REGEX = new RegExp(/\u200B/g);
const ZERO_WIDTH_SPACE_LANGUAGES = ['ZH', 'JA'];
/**
 * Filters out zero width space characters from provided string.
 * This is needed to ensure Chinese and Japanese copies can properly render
 * for pdfMake since there is no zero width space character in ZH and JA 
 * provided glyphs (.ttf files)
 * @param {string} str
 * @returns string without zero width space characters
 */
export const filterZeroWidthSpace = (str = '', language) => {
  if (str && ZERO_WIDTH_SPACE_LANGUAGES.includes(language)) {
    return str
      .replace(ZERO_WIDTH_SPACE_REGEX, '');
  }
  return str;
};

/**
 * Takes languages abbreviation and returns directory of font assets
 * @param {string} lang ['EN', 'ES', ...]
 * @returns {object} object containing file names of 
 */
export const getFontFilenames = (lang) => {
  const regular = get(LANG_FONT_MAP, `${lang}.regular`);
  const bold = get(LANG_FONT_MAP, `${lang}.bold`);

  return {
    regular,
    bold
  };
};

/**
 * Takes strings and either returns them untouched or reverses them for RTL languages
 * @param {boolean} rtl - is the language a right to left language
 * @param {string} text - the text to display
 * @param {number} columnWidth - max chars a single line will fit
 * @returns string
 */
export const rtlSplitReverseMultiLine = (rtl, text = '', columnWidth = 1000) => {
  if (rtl) {
    const englishRegex = /[A-Za-z0-9]+/;
    let output = '';
  
    // Check if the text contains english chars so we can split it up and only reverse the farsi text
    if (englishRegex.test(text)) {
      // Add a marker (***) between english and farsi text so we know where to split the text
      const splitText = text.split(' ').map((word, index, arr) => {
        const wordType = englishRegex.test(word) ? 'EN' : 'FA';
        const prevWordType = englishRegex.test(arr[index - 1]) ? 'EN' : 'FA';
        return wordType !== prevWordType ? `*** ${word}` : word;
      });
    
      const textArray = splitText.join(' ').split('***');

      output = textArray
        .reverse()
        .map(value => englishRegex.test(value) ? value : rtlSplitReverse(true, value))
        .join('');
    } else {
      output = rtlSplitReverse(rtl, text);
    }

    output = reverseNumbersInStr(output);

    if (output.length <= columnWidth) {
      return output;
    }
    else {
      const lineArr = [];
      const textArr = output
        .split(' ')
        .filter(s => s)
        .reverse();

      let newLine = [];
      let chars = 0;

      for (let i = 0; i < textArr.length; i++) {
        const currentStr = textArr[i];
        chars += currentStr.length;

        if (chars < columnWidth) {
          newLine.push(currentStr);
          // Add an extra space after an english word
          if (/[a-z]/i.test(currentStr)) {
            newLine.push(' ');
          }
        } else {
          lineArr.push(['\n'].concat(newLine));
          newLine = [currentStr];
          chars = currentStr.length;
          // Add an extra space after an english word
          if (/[a-z]/i.test(currentStr)) {
            newLine.push(' ');
          }
        }

        if (i === textArr.length - 1) {
          lineArr.push([' '].concat(newLine));
        }
      }

      // Turn arrays back into a single string
      let result = lineArr
        .map(s => s.reverse().join(' '))
        .join(' ');

      // Remove extra spaces
      result = result
        .split(' ')
        .filter((s, index, arr) => {
          return !(!s && !arr[index - 1]);
        })
        .join(' ');
        
      return result;
    }
  }

  return text;
};

/**
 * Takes strings and either returns them untouched or reverses them for RTL languages
 * @param {boolean} rtl - Is the current language a right-to-left language
 * @param {string} str - The text to be rendered on the pdf
 * @param {string} secondaryStr - This parameter is used when the current language is a LTR language or 
 *  the primary str param is a new key (Ex. zkra or zkrb) that does not exist in other translation files. 
 *  If the language is LTR, then we return secondaryStr unless it doesn't exist; then we return str.
 * @returns string
 */
export const rtlSplitReverse = (rtl, str, secondaryStr = '') => {
  if (rtl) {
    let output = (str || secondaryStr)
      .split(' ')
      .reverse()
      .join(' ');
    output += ' ';

    return output;
  }

  return secondaryStr || str;
};

function reverseNumbersInStr(str) {
  // Looking for an integer that is connected to Farsi text
  // If an integer is by itself (surrounded by spaces), leave it alone
  const regex = /[\d]{2,}(?![\s\d-\(\)])/gi;

  if (regex.test(str)) {
    let result = str.split('');

    const matches = [...str.matchAll(/[\d]{2,}(?![\s\d-\(\)])/gi)];
    
    matches.forEach(m => {
      const match = m[0];
      const startIndex = m.index;
      const endIndex = match.length + startIndex;

      const reversedEngText = match
        .split('')
        .reverse()
        .join('')
        .trim();

      result.splice(startIndex, endIndex - startIndex, reversedEngText);
    });

    return result.join('');
  }

  return str;
}